import fs, { write } from "node:fs";
// import https from "node:https";
import path from "node:path";
import {
    downloadImage,
    getFilenameFromUrl,
    type ResponseData,
    randomString,
    sleep,
    success,
    TaskState,
} from "@iflyrpa/share";
import type { AxiosResponse } from "axios";
import FormData from "form-data";
import type { AxiosProxyParams } from "../../types";
import { extractImgTag, getFileState, replaceImgSrc, stringifyError } from "../../utils";
import { classifyPublishError } from "../../utils/errorMessages";
import { Http } from "../../utils/http";
import { reportLogger } from "../../utils/reportLogger";
import type { PublishAction, WeixinPublishParams, WxInteraction, WxWhitelistUserInfo } from "./index";
import { genTopicHTML, type QuotaDetail, QuotaType, scanRetryMaxCount } from "./utils";

type CommonResponse<T = unknown> = T & {
    base_resp?: { err_msg: string; ret: number };
};

const GET_QRCODE_DEFAULT_ERROR = "二维码获取异常，请稍后重试。";

const errnoMap: Record<number | string, string> = {
    200003: "微信公众号号登录状态失效，请重新绑定账号后重试。",
    420001: "封面图不支持GIF，请更换后重试。",
    200002: "上传图片参数错误，请检查后重试。",
    200039: "上传图片高度（像素）与宽度（像素）的乘积不能超过600万，请更换后重试。",
    153201: "不支持发布审核中或转码中的视频",
    200007: "登陆失败，请重新登陆账户后重试。",
    64703: "摘要超出120字长度限制，请修改摘要后重试。",
    "-4": "不支持发布审核中或转码中的视频",
    "-2": "不支持发布审核中或转码中的视频",
    "-1": "系统错误，请注意备份内容后重试",
    770001: "不支持发布审核中或转码中的视频",
    200074: "系统繁忙，请稍后重试！",
    64702: "标题超出64字长度限制，请修改标题后重试。",
    64552: "请检查阅读原文中的链接后重试。",
};

// 不需要直接抛出的异常码，如 154019
const ignoreErrno = [
    154019,
    154011, // 原创校验中
    154008, // 原创校验不通过
    154009, // 原创校验通过
];

// 用户互动类型表（兼容传参文本与页面文本两种写法）
const userTypeMap: Record<string, 1 | 2 | 3> = {
    所有用户: 1,
    所有人: 1,
    已关注的用户: 2,
    已关注的人: 2,
    已关注7天及以上的用户: 3,
    已关注7天及以上的人: 3,
};

const generatorFormData = (data: Record<string, unknown>) => {
    const formData = new URLSearchParams();

    for (const [key, value] of Object.entries(data)) {
        formData.append(key, String(value));
    }

    return formData.toString();
};

export const checkCopyrightStat = async <T>(http: Http, params: WeixinPublishParams, data: Record<string, unknown>) => {
    let resData: T;

    try {
        resData = await http.api({
            method: "post",
            url: "https://mp.weixin.qq.com/cgi-bin/masssend",
            params: {
                action: "get_appmsg_copyright_stat",
                token: params.token,
                lang: "zh_CN",
            },
            data: generatorFormData(data),
        });
    } catch (error) {
        resData = (error as ResponseData)?.data;
    }

    return resData;
};

export const mockAction: PublishAction = async (task, params) => {
    const fingerprint = "4695500bc93ab4ce8fb2692da6564e04";
    const client_time_diff = Number("1743488763") * 1 - Math.floor(new Date().getTime() / 1000);
    const reporter = task.reportService?.repportTaskStatus ?? (() => Promise.resolve());

    const tmpCachePath = task.getTmpPath();
    const headers = {
        "User-Agent":
            "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.36 Edg/134.0.0.0",
        Cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        origin: "https://mp.weixin.qq.com",
        referer: `https://mp.weixin.qq.com/cgi-bin/appmsg?t=media/appmsg_edit&action=edit&isNew=1&type=77&createType=0&token=${params.token}&lang=zh_CN&timestamp=${Date.now()}`,
    };
    const args: AxiosProxyParams = [
        { headers },
        task.logger,
        params.proxyLoc,
        params.accountId,
        "weixin", // 业务标识
    ];
    const http = new Http({ headers });
    const proxyHttp = new Http(...args);

    // 添加响应拦截器
    const baseRespFinder = (response: AxiosResponse<CommonResponse> | undefined) => {
        const responseData = response?.data;
        const msgType = params.saveType === "draft" ? "同步" : "发布";

        // 检查响应中的 errno 字段
        if (
            response &&
            responseData?.base_resp &&
            responseData.base_resp.ret !== 0 &&
            !ignoreErrno.includes(responseData.base_resp.ret)
        ) {
            const errmsg =
                errnoMap[responseData.base_resp.ret] ||
                response.config.defaultErrorMsg ||
                responseData.base_resp.err_msg ||
                `文章${msgType}异常，请稍后重试。`;

            return {
                code: errnoMap[responseData.base_resp.ret] ? 414 : 500,
                message: errmsg,
                data: responseData,
            };
        }
    };
    http.addResponseInterceptor<CommonResponse>(baseRespFinder);
    proxyHttp.addResponseInterceptor<CommonResponse>(baseRespFinder);

    // 0. 校验登录
    await http.api({
        method: "get",
        url: "https://mp.weixin.qq.com/cgi-bin/appmsgpublish",
        params: {
            fingerprint,
            sub: "list",
            begin: 0,
            count: 10,
            query: "",
            type: 101_1_102_103,
            show_type: "",
            free_publish_type: 1_102_103,
            sub_action: "list_ex",
            search_card: 0,
            token: params.token,
            lang: "zh_CN",
            f: "json",
            ajax: 1,
        },
    });

    // 1. 提取并上传正文中的图片
    const uploadImages = async (images: string[]) => {
        const cloudImages: Array<{ cdn_url: string; content: string }> = [];

        // 下载图片到本地
        const localImages = await Promise.all(
            images.map((url) => {
                let fileName = getFilenameFromUrl(url);
                const ext = path.extname(fileName);
                fileName = ext ? fileName : `${fileName}.png`;
                return downloadImage(url, path.join(tmpCachePath, fileName));
            }),
        );

        for (let index = 0; index < images.length; index++) {
            const image = localImages[index];
            const fileState = getFileState(image);

            const formData = new FormData();
            formData.append("id", `WU_FILE_${index + 1}`);
            formData.append("name", fileState.name);
            formData.append("type", fileState.type);
            formData.append("lastModifiedDate", fileState.lastModifiedDate);
            formData.append("size", fileState.size);
            formData.append("file", fs.createReadStream(image));

            const response = await http.api<(typeof cloudImages)[0]>(
                {
                    method: "post",
                    url: `https://mp.weixin.qq.com/cgi-bin/filetransfer?action=upload_material&f=json&writetype=doublewrite&token=${params.token}&lang=zh_CN`,
                    data: formData,
                    defaultErrorMsg: "图片上传异常，请稍后重试发布。",
                },
                { retries: 3, retryDelay: 20, timeout: 2 * 60 * 1000 },
            ); // 增加到2分钟，适应大图片上传

            cloudImages.push(response);
        }

        return cloudImages;
    };

    const originContentImages = extractImgTag(params.content);
    const targetContentImages = await uploadImages(originContentImages);
    // 2. 替换正文中的图片
    const content = replaceImgSrc(
        params.settingInfo.wxTopic && params.settingInfo.wxTopic.length > 0
            ? params.content + genTopicHTML(params.settingInfo.wxTopic)
            : params.content,
        (dom) => {
            if (dom.attribs.src) {
                const idx = originContentImages.findIndex((it) => it === dom.attribs.src.replace(/&amp;/g, "&"));
                dom.attribs.src = targetContentImages[idx].cdn_url;
                dom.attribs.class = "rich_pass wxw-img js_insertlocalimg";
                dom.attribs.style = "";
            }
            return dom;
        },
    );

    // 3. 上传封面图片
    const coverRes = await uploadImages(params.settingInfo.wxCover ? [params.settingInfo.wxCover] : []);

    // 4. 原创声明白名单有效性判断与生成转载白名单
    let oriWhitelist = null;

    if (params.settingInfo?.wxCopyright && params.settingInfo?.wxWhitelist) {
        const _oriWhitelist = params.settingInfo.wxWhitelist;

        if (_oriWhitelist && _oriWhitelist.length > 0) {
            const white_list = [];

            const white_list_user = {
                nickname: "",
                title: "可修改文章",
                openid: "",
                wx_name: "",
                username: "",
                avatar: "",
                can_modify: "1",
                can_hide_source: "0",
                can_reward: "0",
            };

            for (const _user of _oriWhitelist) {
                const userInfo_res = await http.api<{ search_list: WxWhitelistUserInfo[] }>({
                    method: "post",
                    url: "https://mp.weixin.qq.com/cgi-bin/appmsgcopyright?action=searchacct",
                    params: {
                        action: "searchacct",
                    },
                    data: generatorFormData({
                        lang: "zh_CN",
                        f: "json",
                        ajax: 1,
                        token: params.token,
                        random: Math.random().toString(),
                        username: _user,
                    }),
                    defaultErrorMsg: "搜索用户信息失败",
                });

                if (userInfo_res.search_list.length > 0) {
                    const userInfo_resJson = userInfo_res.search_list[0];
                    white_list_user.nickname = userInfo_resJson.nickname;
                    white_list_user.openid = userInfo_resJson.openid;
                    white_list_user.avatar = userInfo_resJson.pic_url;
                    white_list_user.username = userInfo_resJson.username;
                    white_list_user.wx_name = userInfo_resJson.wx_name;
                    white_list.push({ ...white_list_user });
                } else {
                    return { code: 414, message: `白名单用户：${_user} 未搜索到，请确定输入是否正确！`, data: "" };
                }
            }
            oriWhitelist = white_list.length > 0 ? JSON.stringify({ white_list: white_list }) : "";
        }
    }

    // 5. 赞赏设置
    let writerid = null;
    let reply_id = null;
    if (params.settingInfo.wxCopyright && params.settingInfo.wxRewardTarget) {
        async function getWriterList() {
            const res = await http.api({
                method: "get",
                url: "https://mp.weixin.qq.com/acct/writermgr",
                params: {
                    action: "get_writer_list",
                    fingerprint,
                    token: params.token,
                    lang: "zh_CN",
                    f: "json",
                    ajax: 1,
                },
            });

            return res.pageinfo.writerlist || [];
        }

        async function getRewardText(): Promise<{ text: string; reply_id: string }[]> {
            const res = await http.api({
                method: "get",
                url: "https://mp.weixin.qq.com/merchant/reward",
                params: {
                    action: "getautoreply",
                    fingerprint,
                    token: params.token,
                    lang: "zh_CN",
                    f: "json",
                    ajax: 1,
                },
            });

            return res.reply_infos;
        }

        //遍历绑定信息
        for (const _type in params.settingInfo.wxRewardTarget) {
            if (_type === "author") {
                if ((await getWriterList()).length === 0) {
                    // 获取二维码等待用户绑定
                    const rewardbind = await http.api({
                        method: "get",
                        url: "https://mp.weixin.qq.com/acct/writermgr",
                        params: {
                            action: "get_rewardbind_qrcode",
                            random: Math.random().toString(),
                            fingerprint,
                            token: params.token,
                            lang: "zh_CN",
                            f: "json",
                            ajax: 1,
                        },
                        defaultErrorMsg: GET_QRCODE_DEFAULT_ERROR,
                    });

                    const rewardBuffer = Buffer.from(rewardbind.qrcode_base64, "base64");
                    const rewardFilePath = path.join(tmpCachePath, "weixin_reward.jpg");
                    fs.writeFileSync(rewardFilePath, new Uint8Array(rewardBuffer));

                    params.safeQrcodeCallback?.(rewardFilePath);

                    const task_id = rewardbind.task_id;

                    let code = null;

                    for (let i = 0; i < scanRetryMaxCount; i++) {
                        await sleep(2000);

                        const checkScanResult = await http.api<{ qrcode_status: number }>({
                            method: "get",
                            url: "https://mp.weixin.qq.com/acct/writermgr",
                            params: {
                                action: "query_rewardbind_qrcode_status",
                                task_id: task_id,
                                fingerprint,
                                lang: "zh_CN",
                                f: "json",
                                ajax: 1,
                                token: params.token,
                                timespam: Date.now(),
                            },
                        });

                        code = checkScanResult.qrcode_status;

                        if (code === 2 && (await getWriterList()).length > 0) {
                            break;
                        }

                        if (i === scanRetryMaxCount - 1) {
                            return { code: 414, message: "二维码已过期，请重试绑定。", data: "" };
                        }
                    }
                }

                // 判断为赞赏作者
                const _writer = await getWriterList();
                for (const _writer_item of _writer) {
                    if (_writer_item.nickname === params.settingInfo.wxRewardTarget[_type]) {
                        writerid = _writer_item.writerid;
                    }
                }
                if (!writerid) {
                    // 没找到作者id
                    return { code: 414, message: "未匹配到赞赏绑定用户，请确认用户名与绑定是否正常！", data: "" };
                }

                if (params.settingInfo?.wxRewardText) {
                    // 判断文案是否已添加
                    const rewardList = await getRewardText();

                    const matchdID =
                        rewardList.find((reward) => reward.text === params.settingInfo.wxRewardText)?.reply_id ?? null;

                    if (rewardList.length === 0 || !matchdID) {
                        await http.api({
                            method: "post",
                            url: "https://mp.weixin.qq.com/merchant/reward",
                            params: {
                                action: "addautoreply",
                            },
                            data: generatorFormData({
                                type: 1,
                                fingerprint,
                                token: params.token,
                                lang: "zh_CN",
                                f: "json",
                                ajax: 1,
                                text: params.settingInfo.wxRewardText,
                            }),
                        });
                    } else {
                        reply_id = matchdID;
                    }

                    if (!matchdID) {
                        for (const _reply_item of await getRewardText()) {
                            reply_id = (await getRewardText()).find(
                                (reward) => reward.text === params.settingInfo.wxRewardText,
                            )?.reply_id;
                        }
                        if (!reply_id) {
                            return { code: 414, message: "赞赏文案添加失败，请重新检查", data: "" };
                        }
                    }
                }
            } else if (_type === "project") {
                // 判断为公益捐赠
            }
        }
    }
    // 留言设置
    const wxInteractionInfo = (() => {
        const _wxInteraction = {
            need_open_comment0: 0,
            only_fans_can_comment0: 0,
            only_fans_days_can_comment0: 0,
            reply_flag0: 2,
            auto_elect_comment0: 0,
            auto_elect_reply0: 1,
            open_fansmsg0: 0,
        };

        const type = params?.settingInfo?.wxInteraction?.Type ?? "";
        if (type === "public") {
            const _opt = params?.settingInfo?.wxInteraction?.Option;
            if (!_opt || !_opt.Comment || !_opt.reply || _opt.elect_comment == null || _opt.elect_reply == null)
                return null;

            const commentType = userTypeMap[_opt.Comment];
            const replyType = userTypeMap[_opt.reply] + 1;

            // 设置评论权限
            _wxInteraction.need_open_comment0 = 1;
            switch (commentType) {
                case 1:
                    _wxInteraction.only_fans_can_comment0 = 0;
                    _wxInteraction.only_fans_days_can_comment0 = 0;
                    break;
                case 2:
                    _wxInteraction.only_fans_can_comment0 = 1;
                    _wxInteraction.only_fans_days_can_comment0 = 0;
                    break;
                case 3:
                    _wxInteraction.only_fans_can_comment0 = 0;
                    _wxInteraction.only_fans_days_can_comment0 = 1;
                    break;
            }

            // 设置回复权限等级
            _wxInteraction.reply_flag0 = replyType;

            // 精选功能
            _wxInteraction.auto_elect_comment0 = _opt.elect_comment ? userTypeMap[_opt.Comment] : 0;
            _wxInteraction.auto_elect_reply0 = _opt.elect_reply ? userTypeMap[_opt.reply] : 1;

            return _wxInteraction;
        }

        if (type === "private") {
            _wxInteraction.open_fansmsg0 = 1;
            return _wxInteraction;
        }

        // 其他未启用评论功能的情形
        return _wxInteraction;
    })();

    if (!wxInteractionInfo) return { code: 414, message: "互动参数设置错误，请检查参数设置！", data: "" };

    // 4. 保存为草稿
    const draftData = {
        lang: "zh_CN",
        f: "json",
        ajax: 1,
        count: 1,
        operate_from: "Chrome",
        isnew: 0,
        articlenum: 1,
        pre_timesend_set: 0,
        is_finder_video0: 0,
        finder_draft_id0: 0,
        applyori0: 0,
        can_reward0: writerid ? 1 : 0,
        pay_gifts_count0: 0,
        is_video_recommend0: -1,
        writerid0: writerid ? writerid : 0,
        auto_gen_digest0: 0,
        last_choose_cover_from0: 0,
        need_open_comment0: wxInteractionInfo.need_open_comment0,
        only_fans_can_comment0: wxInteractionInfo.only_fans_can_comment0,
        only_fans_days_can_comment0: wxInteractionInfo.only_fans_days_can_comment0,
        reply_flag0: wxInteractionInfo.reply_flag0,
        auto_elect_comment0: wxInteractionInfo.auto_elect_comment0,
        auto_elect_reply0: wxInteractionInfo.auto_elect_reply0,
        reward_reply_id0: params.settingInfo?.wxRewardText ? (reply_id ? reply_id : 0) : 0,
        not_pay_can_comment0: 0,
        data_seq: 0,
        msg_index_id0: "",

        token: params.token,
        AppMsgId: params.appMsgId || "",
        title0: params.title,
        author0: params?.settingInfo?.wxAuthor || "",
        digest0: params?.settingInfo?.wxAbstract || "",
        content0: content || "",
        sourceurl0: params?.settingInfo?.wxOriginalText || "",
        cdn_url0: coverRes[0]?.cdn_url || "",
        copyright_type0: params?.settingInfo?.wxCopyright ? 1 : 0,
        allow_reprint0: params?.settingInfo?.wxCopyright ? 0 : "",
        allow_reprint_modify0: params?.settingInfo?.wxCopyright ? 0 : "",
        ori_white_list0: params?.settingInfo?.wxCopyright ? oriWhitelist : "",
        open_fansmsg0: wxInteractionInfo.open_fansmsg0,
        allow_fast_reprint0: params?.settingInfo?.wxQuickReprint ? 1 : 0,
        disable_recommend0: params?.settingInfo?.wxNotAllowRecommend ? 1 : 0,
        claim_source_type0: params?.settingInfo?.wxCreationSource?.[0] || "",
        appmsg_album_info0: JSON.stringify({
            appmsg_album_infos: params.settingInfo?.wxCollectionInfo ? [params.settingInfo.wxCollectionInfo] : [],
        }),

        masssend_check: 1,
        is_masssend: 1,
    };

    task._timerRecord.PrePublish = Date.now();

    const { appMsgId } = await http.api<{ appMsgId: string }>(
        {
            method: "post",
            url: "https://mp.weixin.qq.com/cgi-bin/operate_appmsg",
            params: {
                token: params.token,
                t: "ajax-response",
                sub: "create",
                type: 77,
                lang: "zh_CN",
            },
            data: generatorFormData(draftData),
            defaultErrorMsg: "创建草稿失败，请稍后重试发布。",
        },
        { timeout: 60 * 1000 },
    );

    // 如果只需要保存为草稿，直接返回 appMsgId
    if (params.saveType === "draft") {
        return { code: 0, data: appMsgId, message: "微信公众号保存草稿成功。" };
    }

    let checkTimes = 0;
    let copyrightStatRes: { base_resp: { ret: number }; list?: string } = { base_resp: { ret: -1 }, list: "" };
    do {
        checkTimes++;
        const copyrightStat = http.api<{ base_resp: { ret: number }; list?: string }>({
            method: "post",
            url: "https://mp.weixin.qq.com/cgi-bin/masssend",
            params: {
                action: "get_appmsg_copyright_stat",
                token: params.token,
                lang: "zh_CN",
            },
            data: generatorFormData({
                token: params.token,
                lang: "zh_CN",
                f: "json",
                ajax: 1,
                fingerprint,
                random: Math.random().toString(),
                first_check: checkTimes === 1 ? 1 : 0,
                type: 10,
                appMsgId,
            }),
        });

        copyrightStatRes = await copyrightStat;
        if (copyrightStatRes.base_resp.ret === 154008 && copyrightStatRes?.list)
            return {
                code: 414,
                message: "文章内容未通过原创校验，请修改后重试！",
                data: JSON.parse(copyrightStatRes.list).list,
            };
    } while (copyrightStatRes.base_resp.ret === 154011 && checkTimes < 10);

    const masssendpage = () => {
        return http.api<CommonResponse<{ operation_seq: string; quota_detail_list: QuotaDetail[] }>>({
            method: "get",
            url: "https://mp.weixin.qq.com/cgi-bin/masssendpage",
            params: {
                token: params.token,
                lang: "zh_CN",
                f: "json",
                ajax: 1,
                preview_appmsgid: appMsgId,
                fingerprint,
                random: Math.random().toString(),
            },
            defaultErrorMsg: "创建草稿失败，请稍后重试发布。",
        });
    };

    // 4. 预览
    let massSendRes = await masssendpage();
    // 判断是否需要同意群发声明
    if (massSendRes.base_resp?.ret === 154019) {
        await http.api({
            method: "post",
            url: "https://mp.weixin.qq.com/cgi-bin/setprotocolsign",
            params: {
                cgi: "setprotocolsign",
                lang: "zh_CN",
                token: params.token,
            },
            data: generatorFormData({
                token: params.token,
                lang: "zh_CN",
                f: "json",
                ajax: 1,
                fingerprint,
                random: Math.random().toString(),
                issigned: 1,
            }),
        });
        massSendRes = await masssendpage();
    }

    // 判断是否还有群发次数
    const { operation_seq, quota_detail_list = [] } = massSendRes;

    // 群发时需要判断是否还有群发次数
    if (params.masssend) {
        const quotaDetail = quota_detail_list.find((it) => it.quota_type === QuotaType.Normal);
        if (!quotaDetail?.quota_item_list[0]?.quota) {
            return { code: 414, message: "今日群发次数用尽，请明天再试。", data: "" };
        }
    }

    // 非群发时会多一个接口
    if (!params.masssend) {
        await http.api({
            method: "post",
            url: "https://mp.weixin.qq.com/cgi-bin/masssend",
            params: {
                t: "ajax-response",
                for_check: 1,
                is_release_publish_page: 1,
                token: params.token,
                lang: "zh_CN",
            },
            data: generatorFormData({
                appmsgid: appMsgId,
                fingerprint,
                token: params.token,
                lang: "zh_CN",
                f: "json",
                ajax: 1,
            }),
        });
    }

    // check
    await http.api({
        method: "post",
        url: "https://mp.weixin.qq.com/cgi-bin/masssend?action=check_same_material",
        data: generatorFormData({
            msgid: appMsgId,
            fingerprint,
            token: params.token,
            lang: "zh_CN",
            f: "json",
            ajax: 1,
            random: Math.random().toString(),
        }),
    });

    const copyrightStatData = {
        token: params.token,
        lang: "zh_CN",
        f: "json",
        ajax: 1,
        random: Math.random().toString(),
        fingerprint,
        first_check: 1,
        type: 10,
        appmsgid: appMsgId,
    };
    for (let i = 0; i < 10; i++) {
        await sleep(1000);
        const lastData = { ...copyrightStatData, first_check: i === 0 ? 1 : 0 };
        const checkCopyrightStatRes = await checkCopyrightStat<{ open_ad_reprint_status?: string }>(
            http,
            params,
            lastData,
        );
        if (checkCopyrightStatRes.open_ad_reprint_status) {
            break;
        }
    }

    await sleep(300);

    await http.api({
        method: "post",
        url: "https://mp.weixin.qq.com/cgi-bin/masssend",
        params: {
            action: "check_ad",
            token: params.token,
            lang: "zh_CN",
        },
        data: generatorFormData({
            appmsg_id: appMsgId,
            fingerprint,
            token: params.token,
            lang: "zh_CN",
            f: "json",
            ajax: 1,
            is_release_publish_page: params.masssend ? 0 : 1, // 群发时为 0，否则为 1
            send_time: params.settingInfo.timer ? params.settingInfo.timer : 0,
            random: Math.random().toString(),
        }),
    });

    await sleep(300);

    await http.api({
        method: "post",
        url: "https://mp.weixin.qq.com/cgi-bin/masssend",
        params: {
            action: "check_music",
        },
        data: generatorFormData({
            appmsgid: appMsgId,
            masssend_check: 1,
            fingerprint,
            token: params.token,
            lang: "zh_CN",
            f: "json",
            ajax: 1,
            random: Math.random().toString(),
        }),
    });

    await sleep(300);

    // 5. 获取群发文章ticket接口
    const getTicketResult = await http.api<{ ticket: string; operation_seq: string }>({
        method: "post",
        url: "https://mp.weixin.qq.com/misc/safeassistant",
        params: {
            token: params.token,
            lang: "zh_CN",
            1: 1,
        },
        data: generatorFormData({
            token: params.token,
            lang: "zh_CN",
            f: "json",
            ajax: 1,
            action: "get_ticket",
            fingerprint,
            random: Math.random().toString(),
        }),
        defaultErrorMsg: GET_QRCODE_DEFAULT_ERROR,
    });

    await sleep(300);

    // 6. 获取群发文章 uuid
    const getUuidResult = await http.api<{ uuid: string; genuuid_res: number }>({
        method: "post",
        url: "https://mp.weixin.qq.com/safe/safeqrconnect",
        params: {
            token: params.token,
            lang: "zh_CN",
            1: 1,
        },
        data: generatorFormData({
            token: params.token,
            lang: "zh_CN",
            f: "json",
            ajax: 1,
            state: 0,
            login_type: "safe_center",
            type: "json",
            ticket: getTicketResult.ticket,
            fingerprint,
            random: Math.random().toString(),
        }),
        defaultErrorMsg: GET_QRCODE_DEFAULT_ERROR,
    });
    await sleep(1000);

    if (getUuidResult.genuuid_res !== 0) {
        return { code: 414, message: GET_QRCODE_DEFAULT_ERROR, data: "" };
    }
    const uuid = getUuidResult.uuid;
    await sleep(1000);

    // 7. 获取验证二维码
    const qrcodeResult = await http.api({
        method: "get",
        url: "https://mp.weixin.qq.com/safe/safeqrcode",
        params: {
            action: "check",
            type: "msgs",
            service_type: 1,
            ticket: getTicketResult.ticket,
            uuid,
            msgid: operation_seq,
            open_email: !!params.settingInfo.timer,
            ...(params.masssend ? {} : { publish_type: 1 }),
        },
        defaultErrorMsg: GET_QRCODE_DEFAULT_ERROR,
        responseType: "arraybuffer",
    });

    const qrcodeBuffer = Buffer.from(qrcodeResult);
    const qrcodeFilePath = path.join(tmpCachePath, `weixin_qrcode_${Date.now()}.jpg`);
    fs.writeFileSync(qrcodeFilePath, new Uint8Array(qrcodeBuffer));

    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");

    if (params.safeQrcodeCallback) {
        params.safeQrcodeCallback?.(qrcodeFilePath);
    } else {
        const data = `data:image/jpeg;base64,${Buffer.from(qrcodeResult).toString("base64")}`;
        await reporter(10041, "请扫码确认发布!", data);

        await updateTaskState?.({
            state: TaskState.WAIT_SCAN,
            result: {
                qrImg: data,
            },
        });
    }

    // 8. 验证二维码结果
    let code = "";
    for (let i = 0; i < scanRetryMaxCount; i++) {
        await sleep(2000);

        const checkScanResult = await http.api<{ code: string; errcode: number }>({
            method: "post",
            url: "https://mp.weixin.qq.com/safe/safeuuid",
            params: {
                token: params.token,
                lang: "zh_CN",
                timespam: Date.now(),
            },
            data: generatorFormData({
                token: params.token,
                lang: "zh_CN",
                f: "json",
                ajax: 1,
                uuid,
                action: "json",
                type: "json",
                fingerprint,
                random: Math.random().toString(),
            }),
        });

        if (checkScanResult.errcode === 405 && checkScanResult.code) {
            code = checkScanResult.code;
            break;
        }

        if (i === scanRetryMaxCount - 1) {
            const message = "二维码已过期，请重试发布。";
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: message,
            });

            return { code: 414, message, data: "" };
        }
    }

    // 非群发时会多一个接口
    if (!params.masssend) {
        await http.api({
            method: "post",
            url: "https://mp.weixin.qq.com/misc/safeassistant",
            params: {
                token: params.token,
                lang: "zh_CN",
                1: 1,
            },
            data: generatorFormData({
                token: params.token,
                lang: "zh_CN",
                f: "json",
                ajax: 1,
                fingerprint,
                random: Math.random().toString(),
                action: "get_uuid",
                uuid,
            }),
        });
    }

    const publishData = generatorFormData({
        token: params.token,
        lang: "zh_CN",
        f: "json",
        ajax: "1",
        fingerprint,
        random: Math.random().toString(),
        ack: "",
        code,
        reprint_info: "",
        reprint_confirm: "0",
        list: "",
        groupid: "",
        sex: "0",
        country: "",
        province: "",
        city: "",
        send_time: params.settingInfo.timer ? params.settingInfo.timer : "0",
        type: "10",
        share_page: "1",
        synctxweibo: "0",
        operation_seq,
        req_id: randomString(32),
        req_time: new Date().getTime() + client_time_diff * 1e3,
        sync_version: "1",
        isFreePublish: params.masssend ? "false" : "true",
        appmsgid: appMsgId,
        isMulti: "0",
        direct_send: "1",
        isNeedCode: "true",
        userType: "1",
    });

    // 9. 发送群发通知
    try {
        await proxyHttp.api<{ code: string; errcode: number; proxyInfo: Record<string, unknown> }>(
            {
                method: "post",
                url: "https://mp.weixin.qq.com/cgi-bin/masssend",
                params: {
                    t: "ajax-response",
                    token: params.token,
                    lang: "zh_CN",
                    ...(params.masssend
                        ? {
                              ...(params.settingInfo.timer
                                  ? {
                                        action: "time_send",
                                    }
                                  : {}),
                          }
                        : { is_release_publish_page: 1 }),
                },
                data: publishData,
                defaultErrorMsg: params.masssend
                    ? "文章群发异常，请尝试关闭群发或稍后重试。"
                    : "文章发布异常，请稍后重试。",
            },
            { retries: 3, retryDelay: 500, timeout: 12 * 1000 },
        );
    } catch (error) {
        const handledError = Http.handleApiError(error);
        // 代理连接超时(599) 与网络/服务端错误(500/502/503/504)：重试耗尽后直接返回分类文案，
        // 不再 throw 走 executeAction 的 RPA 兜底。code 归一为 414，使 executeAction 视为终止结果。
        const classified = classifyPublishError(handledError);
        if (classified) {
            // 用户侧只给分类文案，原始错误信息记进日志；debug 模式下追加代理信息便于排查
            const message = `${classified.userMessage}${task.debug ? ` ${http.proxyInfo}` : ""}`;
            task.logger.error(
                `[weixinPublish] ${classified.category}，直接返回: ${handledError.message} (code=${handledError.code})`,
                stringifyError(handledError),
            );
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: message,
            });
            return { code: 414, data: "", message };
        }
        // 其它错误码维持原有逻辑，抛出以便上层 executeAction 走 RPA 兜底
        throw error;
    }

    if (params.saveType === "publish") {
        reportLogger({
            token: params.huiwenToken || "",
            enverionment: task.enverionment || "development",
            postId: params.articleId,
            eip: proxyHttp.proxyInfo,
            accountId: params.accountId,
            uid: params.uid,
            platform: "weixin",
            publishParams: publishData,
        });
    }

    await updateTaskState?.({
        state: TaskState.SUCCESS,
        result: {
            response: appMsgId,
        },
    });

    return success(appMsgId, `微信公众号发布完成！${task.debug ? ` ${http.proxyInfo}` : ""}`);
};
